import axios from 'axios';
import { getToken, refreshToken } from './services/keycloakService';

const api = axios.create({
    baseURL: 'https://labelcheck.botwizard.ch/api/v1',
    // baseURL: 'http://192.168.1.18:6001/api',
    // baseURL: 'http://localhost:6001/api',
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add request interceptor for Keycloak authentication
api.interceptors.request.use(
    async (config) => {
        // Ensure token is fresh
        try {
            await refreshToken();
        } catch (error) {
            console.warn('Token refresh failed:', error);
        }
        
        const token = getToken();
    if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor to handle authentication errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Keycloak will handle the redirect to login
            window.location.reload();
        }
        return Promise.reject(error);
    }
);

// Generate food image using AI
export const generateFoodImage = async (name, category = null, customPrompt = null) => {
    try {
        const response = await api.post('/foods/generate-image', {
            name,
            category,
            customPrompt
        });
        return response.data;
    } catch (error) {
        console.error('Error generating food image:', error);
        throw error;
    }
};

// Fill nutrition data with AI (without image generation)
export const fillNutritionWithAI = async (name, category = null, languages = null) => {
    try {
        const response = await api.post('/foods/fill-with-ai', {
            name,
            category,
            generateImage: false,
            languages
        });
        return response.data;
    } catch (error) {
        console.error('Error filling nutrition with AI:', error);
        throw error;
    }
};

// Handbook API endpoints
api.getHandbooks = async () => {
    try {
        const response = await api.get('/handbooks');
        return response.data.data;
    } catch (error) {
        console.error('Error fetching handbooks:', error);
        throw error;
    }
};

api.getHandbook = async (id) => {
    try {
        const response = await api.get(`/handbooks/${id}`);
        return response.data.data;
    } catch (error) {
        console.error('Error fetching handbook:', error);
        throw error;
    }
};

api.createHandbook = async (handbookData) => {
    try {
        const response = await api.post('/handbooks', handbookData);
        return response.data.data;
    } catch (error) {
        console.error('Error creating handbook:', error);
        throw error;
    }
};

api.updateHandbook = async (id, handbookData) => {
    try {
        const response = await api.put(`/handbooks/${id}`, handbookData);
        return response.data.data;
    } catch (error) {
        console.error('Error updating handbook:', error);
        throw error;
    }
};

api.deleteHandbook = async (id) => {
    try {
        const response = await api.delete(`/handbooks/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting handbook:', error);
        throw error;
    }
};

api.uploadHandbookCover = async (id, file) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post(`/handbooks/${id}/upload-cover`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data.data;
    } catch (error) {
        console.error('Error uploading handbook cover:', error);
        throw error;
    }
};

// Category API endpoints
api.deleteCategory = async (id) => {
    try {
        const response = await api.delete(`/category/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting category:', error);
        throw error;
    }
};

// Cache management API endpoints
api.getCacheStats = async () => {
    try {
        const response = await api.get('/admin/cache/stats');
        return response.data;
    } catch (error) {
        console.error('Error fetching cache statistics:', error);
        throw error;
    }
};

api.refreshCache = async () => {
    try {
        const response = await api.post('/admin/cache/refresh');
        return response.data;
    } catch (error) {
        console.error('Error refreshing cache:', error);
        throw error;
    }
};

api.clearCache = async () => {
    try {
        const response = await api.post('/admin/cache/clear');
        return response.data;
    } catch (error) {
        console.error('Error clearing cache:', error);
        throw error;
    }
};

// User management admin API endpoints
api.getAdminUsers = async (params = {}) => {
    try {
        const response = await api.get('/admin/users', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching admin users:', error);
        throw error;
    }
};

api.getAdminUserStats = async () => {
    try {
        const response = await api.get('/admin/users/stats');
        return response.data;
    } catch (error) {
        console.error('Error fetching admin user stats:', error);
        throw error;
    }
};

api.getAdminUser = async (userId) => {
    try {
        const response = await api.get(`/admin/users/${userId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching admin user:', error);
        throw error;
    }
};

api.getAdminUserSubscription = async (userId) => {
    try {
        const response = await api.get(`/admin/users/${userId}/subscription`);
        return response.data;
    } catch (error) {
        console.error('Error fetching user subscription:', error);
        throw error;
    }
};

api.grantAdminUserSubscription = async (userId, data) => {
    try {
        const response = await api.post(`/admin/users/${userId}/grant-subscription`, data);
        return response.data;
    } catch (error) {
        console.error('Error granting subscription:', error);
        throw error;
    }
};

// ── Quiz admin API ─────────────────────────────────────────────────────────────

// quizId is optional — when omitted, stats cover all quizzes.
api.getQuizStats = async (quizId) => {
    const response = await api.get('/admin/quiz/stats', { params: quizId ? { quizId } : {} });
    return response.data;
};

api.sendQuizInviteSingle = async (email, lang, quizId = 'quiz1') => {
    const response = await api.post('/admin/quiz/invite/single', { email, lang, quizId });
    return response.data;
};

// rows: [{email, lang}]; quizId selects which quiz the whole batch is invited to.
api.startQuizBulkJob = async (rows, quizId = 'quiz1') => {
    const response = await api.post('/admin/quiz/invite/bulk', { rows, quizId });
    return response.data;
};

api.getQuizBulkJob = async (jobId) => {
    const response = await api.get(`/admin/quiz/invite/bulk/${jobId}`);
    return response.data;
};

api.listQuizBulkJobs = async (params = {}) => {
    const response = await api.get('/admin/quiz/invite/bulk', { params });
    return response.data;
};

api.listQuizInvitations = async (params = {}) => {
    const response = await api.get('/admin/quiz/invitations', { params });
    return response.data;
};

api.listQuizResults = async (params = {}) => {
    const response = await api.get('/admin/quiz/results', { params });
    return response.data;
};

// ── Survey admin API ───────────────────────────────────────────────────────────

// List one row per survey (surveyId, title, responseCount, lastSubmittedAt).
api.getSurveys = async () => {
    const response = await api.get('/admin/surveys');
    return response.data;
};

// Paginated responses for a survey. params: { page, limit, search }.
api.getSurveyResponses = async (surveyId, params = {}) => {
    const response = await api.get(`/admin/surveys/${encodeURIComponent(surveyId)}/responses`, { params });
    return response.data;
};

// Single response with full answer payload.
api.getSurveyResponse = async (id) => {
    const response = await api.get(`/admin/surveys/responses/${id}`);
    return response.data;
};

export default api;